import { useCallback, useState } from "react"
import dayjs from "dayjs"
import { AggregationType } from "~/ui-rtk/constants/charts"

interface KPIExportColumn {
  key: string
  header?: string
}

interface KPIExportTableData {
  [key: string]: string | number | null | undefined
}

export interface UseKPIExportOptions {
  columnConfig: KPIExportColumn[]
  granularity: AggregationType
}

export function useKPIExport({
  columnConfig,
  granularity,
}: UseKPIExportOptions) {
  const [isExporting, setIsExporting] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const exportAllTables = useCallback(
    (
      tables: Array<{ key: string; title: string; data: KPIExportTableData[] }>,
    ) => {
      if (!tables || tables.length === 0) {
        console.warn("No tables to export")
        return
      }

      setIsExporting(true)

      try {
        const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss")
        const granularityLabelMap: Record<AggregationType, string> = {
          [AggregationType.DAILY]: "daily",
          [AggregationType.WEEKLY]: "weekly",
          [AggregationType.MONTHLY]: "monthly",
        }
        const granularityLabel = granularityLabelMap[granularity]
        const filename = `kpi_report_${granularityLabel}_${timestamp}.csv`

        let combinedCSV = ""

        tables.forEach((table, index) => {
          if (!table.data || table.data.length === 0) return

          if (index > 0) {
            combinedCSV += "\n\n"
          }

          combinedCSV += `${table.title}\n`

          const headers = columnConfig.map(
            (col: KPIExportColumn) => col.header || col.key,
          )
          combinedCSV += `${headers.join(",")}\n`

          table.data.forEach((row: KPIExportTableData) => {
            // Current year values
            const values = columnConfig.map((col: KPIExportColumn) => {
              const value = row[col.key]
              if (value === null || value === undefined) return ""
              if (typeof value === "string" && value.includes(",")) {
                return `"${value}"`
              }
              return value
            })
            combinedCSV += `${values.join(",")}\n`

            // Check if this row has LY data
            const hasLYData = columnConfig.some(
              col =>
                col.key !== "metric" &&
                row[`${col.key}_yoy_previous`] !== undefined,
            )

            if (hasLYData) {
              // LY row
              const lyValues = columnConfig.map((col: KPIExportColumn) => {
                if (col.key === "metric") {
                  const metricName = row[col.key]
                  return `"${metricName} (LY)"`
                }
                const value = row[`${col.key}_yoy_previous`]
                if (value === null || value === undefined) return ""
                return value
              })
              combinedCSV += `${lyValues.join(",")}\n`

              // YoY % change row
              const yoyValues = columnConfig.map((col: KPIExportColumn) => {
                if (col.key === "metric") {
                  const metricName = row[col.key]
                  return `"${metricName} (YoY %)"`
                }
                const value = row[`${col.key}_yoy_change`]
                if (value === null || value === undefined) return ""
                if (typeof value === "number") {
                  return `${value.toFixed(1)}%`
                }
                return value
              })
              combinedCSV += `${yoyValues.join(",")}\n`
            }
          })
        })

        const BOM = "\uFEFF"
        const blob = new Blob([BOM + combinedCSV], {
          type: "text/csv;charset=utf-8",
        })

        const link = document.createElement("a")
        const url = URL.createObjectURL(blob)
        link.href = url
        link.download = filename
        link.style.display = "none"

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        // Show success dialog after successful export with a small delay to ensure render
        setTimeout(() => {
          setShowSuccessDialog(true)
        }, 100)
      } catch (error) {
        console.error("Failed to export all tables:", error)
      } finally {
        setIsExporting(false)
      }
    },
    [columnConfig, granularity],
  )

  const handleCloseSuccessDialog = useCallback(() => {
    setShowSuccessDialog(false)
  }, [])

  return {
    isExporting,
    exportAllTables,
    showSuccessDialog,
    handleCloseSuccessDialog,
  }
}
